// Portal del Empleado (autoservicio): mis roles de pago, mis vacaciones,
// mis solicitudes y mis datos + certificado laboral. Menú "Portal Empleado"
// (solo perfil `empleado`; identidad = partner_id del JWT vía RPCs nom_portal_*).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, FileText, Loader2, LogIn, LogOut, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Imprimible, PrintButton } from '@/components/print/DocPrint';
import { getErrorMessage } from '@/lib/api/client';
import { hotelRpc } from '@/features/hotel/shared';
import {
  Campo, DIAS_SEMANA, MESES, Modal, PARENTESCOS, TIPOS_LICENCIA, descargarPdfBase64, fecha, fieldCls, money, nomRpc, TIPOS_SOLICITUD,
  type CargasData, type PortalAsistenciaData, type PortalCertificado, type PortalLicencia, type PortalPerfil, type PortalRol, type PortalVacacionesData, type SolicitudRow,
  TIPOS_ARCO, ESTADOS_ARCO, type PortalArco, type PortalAnticipo,
} from './shared';
import { LOCALE } from '@/lib/utils/regional';

const ESTADO_SOL: Record<string, string> = { pendiente: 'bg-pendiente-soft text-pendiente', aprobada: 'bg-estable-soft text-estable', rechazada: 'bg-urgente-soft text-urgente' };
const ORIGEN_ANTICIPO: Record<string, string> = { manual: 'Manual', quincena: 'Quincena', semana: 'Semana' };

// ---------------------------------------------------------------- Mis roles
export function MisRoles() {
  const { data: roles = [], isFetching } = useQuery<PortalRol[]>({
    queryKey: ['portal-roles'],
    queryFn: () => nomRpc<PortalRol[]>('nom_portal_roles', {}),
  });
  const { data: anticipos = [] } = useQuery<PortalAnticipo[]>({
    queryKey: ['portal-anticipos'],
    queryFn: () => nomRpc<PortalAnticipo[]>('nom_portal_anticipos', {}),
  });
  const [sel, setSel] = useState<PortalRol | null>(null);

  if (sel) {
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setSel(null)}>← Volver</Button>
          <h1 className="text-xl font-semibold">Rol de pago — {MESES[sel.mes - 1]} {sel.anio}</h1>
          <PrintButton />
        </div>
        <Imprimible titulo="Rol de Pago Individual" subtitulo={`${MESES[sel.mes - 1]} ${sel.anio}`}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
              <div><span className="text-muted-foreground">Cargo: </span>{sel.cargo ?? '—'}</div>
              <div><span className="text-muted-foreground">Departamento: </span>{sel.departamento ?? '—'}</div>
              <div><span className="text-muted-foreground">Días trabajados: </span>{sel.dias}</div>
              <div><span className="text-muted-foreground">Sueldo base: </span><span className="font-mono">{money(sel.sueldo_base)}</span></div>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {sel.rubros.map((r, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="py-1.5 pr-3">{r.descripcion}</td>
                    <td className={`py-1.5 pr-3 text-right font-mono tabular-nums ${r.tipo === 'egreso' ? 'text-urgente' : ''}`}>
                      {r.tipo === 'egreso' ? '−' : ''}{money(r.valor)}
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold"><td className="py-1.5 pr-3 text-right">Total ingresos</td><td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(sel.ingresos)}</td></tr>
                <tr className="font-semibold"><td className="py-1.5 pr-3 text-right">Total egresos</td><td className="py-1.5 pr-3 text-right font-mono tabular-nums">−{money(sel.egresos)}</td></tr>
                <tr className="text-base font-bold"><td className="py-2 pr-3 text-right">NETO RECIBIDO</td><td className="py-2 pr-3 text-right font-mono tabular-nums">{money(sel.neto)}</td></tr>
              </tbody>
            </table>
          </div>
        </Imprimible>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Mis roles de pago</h1>
      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
      <Card>
        <CardContent className="overflow-x-auto pt-4">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Período</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3 text-right">Ingresos</th>
              <th className="py-2 pr-3 text-right">Egresos</th><th className="py-2 pr-3 text-right">Neto</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {roles.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Aún no tienes roles de pago liquidados</td></tr>}
              {roles.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => setSel(r)}
                    role="button" tabIndex={0} onKeyDown={(ev) => { if (ev.key === 'Enter') setSel(r); }}>
                  <td className="py-2 pr-3">{MESES[r.mes - 1]} {r.anio}</td>
                  <td className="py-2 pr-3">{r.estado === 'pagado' ? `Pagado ${fecha(r.fecha_pagado)}` : 'Cerrado'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.ingresos)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(r.egresos)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold">{money(r.neto)}</td>
                  <td className="py-2 text-right"><FileText className="w-4 h-4 inline text-muted-foreground" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {anticipos.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Mis anticipos</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <p className="text-xs text-muted-foreground mb-2">
              Pagos por adelantado (semanales o quincenales). Se descuentan en el rol del mes; «Descontado» = ya reflejado en tu rol.
            </p>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Fecha de pago</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Período</th>
                <th className="py-2 pr-3 text-right">Monto</th><th className="py-2 pr-3">Estado</th>
              </tr></thead>
              <tbody>
                {anticipos.map((a) => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono">{fecha(a.fecha)}</td>
                    <td className="py-2 pr-3">{ORIGEN_ANTICIPO[a.origen] ?? a.origen}</td>
                    <td className="py-2 pr-3 font-mono">{a.periodo ?? '—'}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(a.monto)}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${a.estado === 'descontado' ? 'bg-estable-soft text-estable' : 'bg-pendiente-soft text-pendiente'}`}>
                        {a.estado === 'descontado' ? 'Descontado' : 'Pendiente'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ------------------------------------------------------------ Mis vacaciones
export function MisVacaciones() {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery<PortalVacacionesData>({
    queryKey: ['portal-vacaciones'],
    queryFn: () => nomRpc<PortalVacacionesData>('nom_portal_vacaciones', {}),
  });
  const [abierto, setAbierto] = useState(false);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const dias = desde && hasta && hasta >= desde
    ? Math.round((new Date(hasta).getTime() - new Date(desde).getTime()) / 86400000) + 1 : 0;

  const solicitar = useMutation({
    mutationFn: () => nomRpc('nom_portal_solicitar', { _tipo: 'vacaciones', _datos: { desde, hasta } }),
    onSuccess: () => {
      toast.success('Solicitud enviada; Talento Humano la revisará'); setAbierto(false);
      void qc.invalidateQueries({ queryKey: ['portal-solicitudes'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Mis vacaciones</h1>
        <Button onClick={() => { setDesde(''); setHasta(''); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Solicitar vacaciones</Button>
      </div>
      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[['Ingreso', fecha(data.saldo.fecha_ingreso)], ['Días ganados', Number(data.saldo.dias_ganados).toFixed(1)],
              ['Días gozados', Number(data.saldo.dias_gozados).toFixed(1)], ['Saldo disponible', Number(data.saldo.saldo).toFixed(1)]].map(([k, v]) => (
              <Card key={k}><CardContent className="py-3"><div className="text-xs text-muted-foreground">{k}</div><div className="text-lg font-semibold font-mono tabular-nums">{v}</div></CardContent></Card>
            ))}
          </div>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Vacaciones gozadas</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Desde</th><th className="py-2 pr-3">Hasta</th><th className="py-2 pr-3 text-right">Días</th><th className="py-2 pr-3">Observación</th>
                </tr></thead>
                <tbody>
                  {data.goces.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Aún no registras vacaciones</td></tr>}
                  {data.goces.map((g, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-2 pr-3 font-mono">{fecha(g.desde)}</td>
                      <td className="py-2 pr-3 font-mono">{fecha(g.hasta)}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{Number(g.dias)}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{g.observacion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
      {abierto && (
        <Modal title="Solicitar vacaciones" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="text-sm">Saldo disponible: <span className="font-mono font-semibold">{Number(data?.saldo.saldo ?? 0).toFixed(1)} días</span></div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Desde *"><input type="date" className={fieldCls} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
              <Campo label="Hasta *"><input type="date" className={fieldCls} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
            </div>
            {dias > 0 && <div className="text-sm">{dias} días calendario (Art. 69 CT: incluye no laborables)</div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={solicitar.isPending} disabled={dias <= 0} onClick={() => solicitar.mutate()}>Enviar solicitud</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------------------ Mis solicitudes
function datosSolicitud(s: SolicitudRow): string {
  if (s.tipo === 'vacaciones') return `${s.datos.desde} → ${s.datos.hasta}`;
  if (s.tipo === 'anticipo') return `${money(Number(s.datos.monto))}${s.datos.motivo ? ` — ${s.datos.motivo}` : ''}`;
  return `${s.datos.fecha} (${s.datos.dias} día/s)${s.datos.motivo ? ` — ${s.datos.motivo}` : ''}`;
}

export function MisSolicitudes() {
  const qc = useQueryClient();
  const { data: rows = [], isFetching } = useQuery<SolicitudRow[]>({
    queryKey: ['portal-solicitudes'],
    queryFn: () => nomRpc<SolicitudRow[]>('nom_portal_solicitudes', {}),
  });
  const [abierto, setAbierto] = useState(false);
  const [tipo, setTipo] = useState<'anticipo' | 'permiso'>('anticipo');
  const [monto, setMonto] = useState('');
  const [fechaP, setFechaP] = useState('');
  const [dias, setDias] = useState('1');
  const [motivo, setMotivo] = useState('');

  const solicitar = useMutation({
    mutationFn: () => nomRpc('nom_portal_solicitar', {
      _tipo: tipo,
      _datos: tipo === 'anticipo' ? { monto: Number(monto), motivo: motivo || null }
        : { fecha: fechaP, dias: Number(dias), motivo: motivo || null, remunerado: true },
    }),
    onSuccess: () => { toast.success('Solicitud enviada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['portal-solicitudes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Mis solicitudes</h1>
        <Button onClick={() => { setMonto(''); setFechaP(''); setDias('1'); setMotivo(''); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Nueva solicitud</Button>
      </div>
      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
      <Card>
        <CardContent className="overflow-x-auto pt-4">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Detalle</th>
              <th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Respuesta</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin solicitudes</td></tr>}
              {rows.map((s) => (
                <tr key={s.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono">{fecha(s.created_at)}</td>
                  <td className="py-2 pr-3">{TIPOS_SOLICITUD[s.tipo]}</td>
                  <td className="py-2 pr-3">{datosSolicitud(s)}</td>
                  <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-xs ${ESTADO_SOL[s.estado]}`}>{s.estado}</span></td>
                  <td className="py-2 pr-3 text-muted-foreground">{s.respuesta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nueva solicitud" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Tipo *">
              <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value as 'anticipo' | 'permiso')}>
                <option value="anticipo">Anticipo de sueldo</option>
                <option value="permiso">Permiso</option>
              </select>
            </Campo>
            {tipo === 'anticipo' && (
              <Campo label="Monto ($) *"><input type="number" min="0" step="0.01" className={fieldCls} value={monto} onChange={(e) => setMonto(e.target.value)} /></Campo>
            )}
            {tipo === 'permiso' && (
              <div className="grid grid-cols-2 gap-3">
                <Campo label="Fecha *"><input type="date" className={fieldCls} value={fechaP} onChange={(e) => setFechaP(e.target.value)} /></Campo>
                <Campo label="Días *"><input type="number" min="0.5" step="0.5" className={fieldCls} value={dias} onChange={(e) => setDias(e.target.value)} /></Campo>
              </div>
            )}
            <Campo label="Motivo"><input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={solicitar.isPending}
                disabled={tipo === 'anticipo' ? !Number(monto) : (!fechaP || !Number(dias))}
                onClick={() => solicitar.mutate()}>Enviar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Mis datos
export function MisDatos() {
  const { data, isFetching } = useQuery<PortalPerfil>({
    queryKey: ['portal-perfil'],
    queryFn: () => nomRpc<PortalPerfil>('nom_portal_perfil', {}),
  });
  const [verCertificado, setVerCertificado] = useState(false);
  const { data: cert } = useQuery<PortalCertificado>({
    queryKey: ['portal-certificado'],
    queryFn: () => nomRpc<PortalCertificado>('nom_portal_certificado', {}),
    enabled: verCertificado,
  });
  const e = data?.empleado;
  const c = data?.contrato;
  const { data: cargas } = useQuery<CargasData>({
    queryKey: ['portal-cargas'],
    queryFn: () => nomRpc<CargasData>('nom_portal_cargas', {}),
  });
  const { data: licencias = [] } = useQuery<PortalLicencia[]>({
    queryKey: ['portal-licencias'],
    queryFn: () => nomRpc<PortalLicencia[]>('nom_portal_licencias', {}),
  });
  const qcArco = useQueryClient();
  const { data: arco = [] } = useQuery<PortalArco[]>({
    queryKey: ['portal-arco'],
    queryFn: () => nomRpc<PortalArco[]>('nom_portal_arco', {}),
  });
  const [arcoTipo, setArcoTipo] = useState('acceso');
  const [arcoDesc, setArcoDesc] = useState('');
  const crearArco = useMutation({
    mutationFn: () => nomRpc('nom_portal_arco_crear', { _data: { tipo: arcoTipo, descripcion: arcoDesc } }),
    onSuccess: () => { toast.success('Solicitud enviada a Talento Humano'); setArcoDesc(''); void qcArco.invalidateQueries({ queryKey: ['portal-arco'] }); },
    onError: (e2) => toast.error(getErrorMessage(e2)),
  });
  const descargarContrato = useMutation({
    mutationFn: () => nomRpc<{ pdf: string; filename: string } | null>('nom_portal_contrato_pdf', {}),
    onSuccess: (d) => { if (d?.pdf) descargarPdfBase64(d.pdf, d.filename); else toast.error('Tu contrato aún no tiene PDF firmado; solicítalo a Talento Humano'); },
    onError: (e2) => toast.error(getErrorMessage(e2)),
  });

  if (verCertificado && cert?.empleado) {
    const hoy = new Date().toLocaleDateString(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
    return (
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setVerCertificado(false)}>← Volver</Button>
          <h1 className="text-xl font-semibold">Certificado laboral</h1>
          <PrintButton />
        </div>
        <Imprimible titulo="Certificado Laboral">
          <div className="space-y-6 text-sm leading-relaxed max-w-2xl mx-auto py-6">
            <p className="font-semibold text-center">A QUIEN CORRESPONDA</p>
            <p>
              {cert.empresa?.nombre}{cert.empresa?.ruc ? `, RUC ${cert.empresa.ruc},` : ''} certifica que
              {' '}<span className="font-semibold">{cert.empleado.razon_social}</span>, con cédula de identidad
              {' '}<span className="font-mono">{cert.empleado.identificacion}</span>,
              {cert.contrato?.estado === 'vigente' ? ' presta sus servicios' : ' prestó sus servicios'} en esta empresa
              {cert.contrato?.cargo ? <> desempeñando el cargo de <span className="font-semibold">{cert.contrato.cargo}</span></> : null}
              {cert.contrato ? <>, desde el {fecha(cert.contrato.fecha_inicio)}, bajo contrato de tipo {cert.contrato.tipo} a jornada {cert.contrato.jornada} ({Number(cert.contrato.horas_semanales)} horas semanales)</> : null}.
            </p>
            <p>El presente certificado se emite a solicitud del interesado, para los fines que estime convenientes.</p>
            <p>{cert.empresa?.direccion ? `${cert.empresa.direccion}, ` : ''}{hoy}.</p>
            <div className="pt-16 text-center">
              <div className="border-t inline-block px-16 pt-2">Talento Humano<br />{cert.empresa?.nombre}</div>
            </div>
          </div>
        </Imprimible>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Mis datos</h1>
        <div className="flex gap-2">
          <Button variant="outline" isLoading={descargarContrato.isPending} onClick={() => descargarContrato.mutate()}><FileText className="w-4 h-4 mr-1" /> Mi contrato (PDF firmado)</Button>
          <Button variant="outline" onClick={() => setVerCertificado(true)}><FileText className="w-4 h-4 mr-1" /> Certificado laboral</Button>
        </div>
      </div>
      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
      {e && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Datos personales</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1.5">
              {[['Nombre', e.razon_social], ['Cédula', e.identificacion], ['Nacimiento', fecha(e.fecha_nacimiento)],
                ['Género', e.genero ?? '—'], ['Estado civil', (e.estado_civil ?? '—').replace(/_/g, ' ')],
                ['Cargas familiares', String(e.cargas_familiares)], ['Email', e.email ?? '—'],
                ['Celular', e.celular ?? '—'], ['Dirección', e.direccion ?? '—'], ['Ciudad', e.ciudad ?? '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border/40 pb-1 last:border-0">
                  <span className="text-muted-foreground">{k}</span><span className="text-right">{v}</span>
                </div>
              ))}
              <p className="text-xs text-muted-foreground pt-2">¿Datos desactualizados? Comunícalo a Talento Humano.</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Datos laborales y de pago</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-1.5">
              {[['Código', e.codigo ?? '—'], ['Cargo', c?.cargo ?? '—'], ['Departamento', c?.departamento ?? '—'],
                ['Contrato', c ? `${c.tipo} desde ${fecha(c.fecha_inicio)}` : '—'],
                ['Jornada', c ? `${c.jornada} (${Number(c.horas_semanales)} h/semana)` : '—'],
                ['Sueldo', c ? money(c.sueldo) : '—'],
                ['Banco', e.banco_nombre ?? '—'], ['Cuenta', e.banco_cuenta ? `${e.banco_tipo_cuenta ?? ''} ${e.banco_cuenta}` : '—'],
                ['Contacto de emergencia', e.contacto_emergencia_nombre ? `${e.contacto_emergencia_nombre} (${e.contacto_emergencia_telefono ?? 's/tel'})` : '—'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border/40 pb-1 last:border-0">
                  <span className="text-muted-foreground">{k}</span><span className="text-right">{v}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {cargas && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Mis cargas familiares</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded-full bg-estable-soft text-estable">Válidas para utilidades: {cargas.validas_utilidades}</span>
              <span className="px-2 py-1 rounded-full bg-estable-soft text-estable">Válidas para rebaja de IR: {cargas.validas_ir}</span>
            </div>
            {cargas.cargas.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tienes cargas familiares registradas.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3">Parentesco</th><th className="py-2 pr-3">Nombre</th>
                  <th className="py-2 pr-3">Nacimiento</th><th className="py-2 pr-3">Acreditada</th>
                </tr></thead>
                <tbody>
                  {cargas.cargas.map((cg) => (
                    <tr key={cg.id} className="border-b last:border-0">
                      <td className="py-2 pr-3">{PARENTESCOS[cg.parentesco]}</td>
                      <td className="py-2 pr-3">{cg.nombre}</td>
                      <td className="py-2 pr-3 font-mono">{cg.fecha_nacimiento ? fecha(cg.fecha_nacimiento) : '—'}</td>
                      <td className="py-2 pr-3">{cg.acreditada
                        ? <span className="px-2 py-0.5 rounded-full text-xs bg-estable-soft text-estable">Sí</span>
                        : <span className="px-2 py-0.5 rounded-full text-xs bg-pendiente-soft text-pendiente">Falta documento</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="text-xs text-muted-foreground">
              Para registrar o actualizar tus cargas (nacimientos, matrimonio, acreditaciones), presenta los
              documentos de respaldo a Talento Humano.
            </p>
          </CardContent>
        </Card>
      )}

      {licencias.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Mis licencias</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Desde</th><th className="py-2 pr-3">Hasta</th>
                <th className="py-2 pr-3 text-right">Días</th><th className="py-2 pr-3">Estado</th>
              </tr></thead>
              <tbody>
                {licencias.map((li, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-3">{TIPOS_LICENCIA[li.tipo]?.label ?? li.tipo}</td>
                    <td className="py-2 pr-3 font-mono">{fecha(li.desde)}</td>
                    <td className="py-2 pr-3 font-mono">{fecha(li.hasta)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{li.dias}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${li.vigente ? 'bg-estable-soft text-estable' : 'bg-muted text-muted-foreground'}`}>
                        {li.vigente ? 'Vigente' : 'Concluida/Programada'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Mis derechos de datos (LOPDP)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Puedes ejercer tus derechos sobre tus datos personales: acceso, rectificación, eliminación,
            oposición, portabilidad o suspensión del tratamiento. Talento Humano responde en 15 días.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <select className={`${fieldCls} w-56`} value={arcoTipo} onChange={(ev) => setArcoTipo(ev.target.value)} aria-label="Tipo de derecho">
              {Object.entries(TIPOS_ARCO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input className={`${fieldCls} flex-1 min-w-[220px]`} value={arcoDesc} placeholder="Describe tu solicitud"
              onChange={(ev) => setArcoDesc(ev.target.value)} />
            <Button disabled={!arcoDesc.trim() || crearArco.isPending} onClick={() => crearArco.mutate()}>Enviar solicitud</Button>
          </div>
          {arco.length > 0 && (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Derecho</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Respuesta</th>
              </tr></thead>
              <tbody>
                {arco.map((a) => (
                  <tr key={a.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3 font-mono whitespace-nowrap">{fecha(a.created_at)}</td>
                    <td className="py-2 pr-3">{TIPOS_ARCO[a.tipo] ?? a.tipo}</td>
                    <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-xs ${a.estado === 'atendida' ? 'bg-estable-soft text-estable' : a.estado === 'rechazada' ? 'bg-muted text-muted-foreground' : 'bg-pendiente-soft text-pendiente'}`}>{ESTADOS_ARCO[a.estado] ?? a.estado}</span></td>
                    <td className="py-2 pr-3 text-muted-foreground">{a.respuesta ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------ Mi asistencia
export function MiAsistencia() {
  const qc = useQueryClient();
  const { data, isFetching } = useQuery<PortalAsistenciaData>({
    queryKey: ['portal-asistencia'],
    queryFn: () => nomRpc<PortalAsistenciaData>('nom_portal_marcaciones', {}),
  });
  const marcar = useMutation({
    mutationFn: () => nomRpc<{ accion: string; hora: string }>('nom_portal_marcar', {}),
    onSuccess: (r) => {
      toast.success(r.accion === 'entrada' ? `Entrada registrada a las ${r.hora}` : `Salida registrada a las ${r.hora}`);
      void qc.invalidateQueries({ queryKey: ['portal-asistencia'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const abierta = data?.abierta ?? null;

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Mi asistencia</h1>
      {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}

      <Card>
        <CardContent className="py-6 flex flex-col items-center gap-3">
          {data?.turno ? (
            <div className="text-sm text-muted-foreground">
              Turno <span className="font-medium text-foreground">{data.turno.nombre}</span>
              {' '}· <span className="font-mono">{data.turno.hora_entrada.slice(0, 5)}–{data.turno.hora_salida.slice(0, 5)}</span>
              {' '}· {(data.turno.dias ?? []).map((d) => DIAS_SEMANA[d - 1]).join(' ')}
            </div>
          ) : (
            <div className="text-sm text-pendiente">Aún no tienes un turno asignado; consulta a Talento Humano.</div>
          )}
          {abierta && (
            <div className="text-sm">Jornada abierta desde las <span className="font-mono font-semibold">{abierta.entrada}</span> ({fecha(abierta.fecha)})</div>
          )}
          <Button size="lg" isLoading={marcar.isPending} onClick={() => marcar.mutate()}>
            {abierta ? <LogOut className="w-5 h-5 mr-2" /> : <LogIn className="w-5 h-5 mr-2" />}
            {abierta ? 'Marcar SALIDA' : 'Marcar ENTRADA'}
          </Button>
          <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Se registra la hora del servidor</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Mis marcaciones del mes</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Entrada</th><th className="py-2 pr-3">Salida</th>
              <th className="py-2 pr-3 text-right">Horas</th><th className="py-2 pr-3 text-right">Atraso</th>
            </tr></thead>
            <tbody>
              {(data?.marcaciones ?? []).length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin marcaciones este mes</td></tr>}
              {(data?.marcaciones ?? []).map((m, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-mono">{fecha(m.fecha)}</td>
                  <td className="py-2 pr-3 font-mono">{m.entrada}</td>
                  <td className="py-2 pr-3 font-mono">{m.salida ?? <span className="text-pendiente">Abierta</span>}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{m.horas != null ? Number(m.horas) : '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{m.atraso_min ? `${m.atraso_min} min` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <MisLimpiezas />
    </div>
  );
}

// 950: derecho de acceso de la camarera a sus registros de housekeeping
// (hotel.hk_portal_mis_registros). Si no es camarera del hotel no se muestra.
interface MisLimpiezasData {
  camarera: string | null; desde?: string;
  tareas?: { fecha: string; habitacion: string; tipo: string; creditos: number; estado: string; inicio: string | null; fin: string | null; minutos: number | null; relimpieza: number }[];
  inspecciones?: { fecha: string; habitacion: string; puntaje: number; resultado: string; defectos: string[] | null; nota: string | null; observacion: string | null }[];
  bonos?: { mes: string; monto: number }[];
  resumen_mensual?: { mes: string; tareas: number; creditos: number; inspecciones: number; aprobadas: number }[];
}
function MisLimpiezas() {
  const { data } = useQuery<MisLimpiezasData | null>({
    queryKey: ['portal-hk'],
    queryFn: async () => { try { return await hotelRpc<MisLimpiezasData>('hk_portal_mis_registros', { _desde: null }); } catch { return null; } },
  });
  if (!data?.camarera) return null;
  const hora = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' }) : '—');
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Mis limpiezas (housekeeping)</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Lo que el hotel registra de su trabajo de los últimos 60 días: hora de inicio y fin de cada habitación e inspecciones. No se usa para descontar su sueldo.
          Si algo no es correcto, pida la corrección a la administración.
        </p>
        {!!data.bonos?.length && <div className="text-xs">Bonos recibidos: {data.bonos.map((b) => `${b.mes}: $${Number(b.monto).toFixed(2)}`).join(' · ')}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b text-left text-muted-foreground"><th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Fecha</th><th className="py-1 pr-2">Hab.</th><th className="py-1 pr-2">Tipo</th><th className="py-1 pr-2">Inicio</th><th className="py-1 pr-2">Fin</th><th className="py-1 pr-2 text-right">Min</th></tr></thead>
            <tbody>
              {(data.tareas ?? []).length === 0 && <tr><td colSpan={7} className="py-3 text-center text-muted-foreground">Sin registros</td></tr>}
              {(data.tareas ?? []).map((t, i) => (
                <tr key={`${t.fecha}-${t.habitacion}`} className="border-b last:border-0"><td className="py-1 pr-2 text-muted-foreground">{i + 1}</td><td className="py-1 pr-2">{t.fecha}</td>
                  <td className="py-1 pr-2">{t.habitacion}</td><td className="py-1 pr-2">{t.tipo}{t.relimpieza ? ' · repaso' : ''}</td><td className="py-1 pr-2">{hora(t.inicio)}</td>
                  <td className="py-1 pr-2">{hora(t.fin)}</td><td className="py-1 pr-2 text-right tabular-nums">{t.minutos ?? '—'}</td></tr>
              ))}
            </tbody>
            {(data.tareas ?? []).length > 0 && <tfoot><tr className="border-t-2 font-semibold"><td colSpan={6} className="py-1">Total: {(data.tareas ?? []).length}</td>
              <td className="py-1 pr-2 text-right tabular-nums">{(data.tareas ?? []).reduce((a, t) => a + (t.minutos ?? 0), 0)}</td></tr></tfoot>}
          </table>
        </div>
        {!!data.inspecciones?.length && (
          <div className="space-y-1">
            <div className="text-xs font-semibold">Inspecciones</div>
            {data.inspecciones.map((n) => (
              <div key={n.fecha} className="rounded border px-2 py-1 text-xs">
                {new Date(n.fecha).toLocaleDateString(LOCALE)} · Hab. {n.habitacion} · {n.puntaje} puntos · {n.resultado === 'aprobada' ? 'aprobada' : 'repaso'}
                {n.defectos?.length ? ` · ${n.defectos.join(', ')}` : ''}{n.observacion ? ` · Su observación: ${n.observacion}` : ''}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

